import {
  Alert,
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
} from "antd";
import { useEffect, useState } from "react";
import {
  getCompanyDepartments,
  getCompanyFiscalPeriods,
  addOrganizationalMetric,
  updateOrganizationalMetric,
} from "../../repository/data.repository";
import {
  MAX_PAGE_SIZE,
  MetricType,
  OrganizationalMetricEntry,
} from "../../repository/data.default";

const METRIC_TYPES: MetricType[] = [
  "Absenteeism",
  "Turnover",
  "Grievance",
  "Accident",
];

interface Option {
  readonly label: string;
  readonly value: string;
}

export interface OrganizationalMetricFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update";
  readonly initialValues?: OrganizationalMetricEntry;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function OrganizationalMetricFormDialog(
  props: OrganizationalMetricFormDialogProps,
) {
  const [form] = Form.useForm();
  const metricType: MetricType | undefined = Form.useWatch("metricType", form);

  const [departments, setDepartments] = useState<Option[]>([]);
  const [periods, setPeriods] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!props.open) {
      form.resetFields();
      setError(false);
      return;
    }
    // Auto-scoped to the manager's company server-side.
    const scoped = {
      searchQuery: { conditions: [] },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
    };
    getCompanyDepartments(scoped)
      .then(({ data }) =>
        setDepartments(data.map((d) => ({ label: d.name, value: d.uid }))),
      )
      .catch(() => setDepartments([]));
    getCompanyFiscalPeriods(scoped)
      .then(({ data }) =>
        setPeriods(data.map((p) => ({ label: p.name, value: p.uid }))),
      )
      .catch(() => setPeriods([]));
    if (props.initialValues) {
      form.setFieldsValue(props.initialValues);
    }
  }, [props.open]);

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    form
      .validateFields()
      .then((v) => {
        // companyUid is stamped server-side from the manager's JWT.
        const payload = {
          departmentUid: v.departmentUid,
          companyFiscalPeriodUid: v.companyFiscalPeriodUid,
          metricType: v.metricType as MetricType,
          headcount: v.headcount ?? 0,
          scheduledWorkdays: v.scheduledWorkdays ?? null,
          eventCount: v.eventCount ?? 0,
          voluntaryDepartures: v.voluntaryDepartures ?? null,
          involuntaryDepartures: v.involuntaryDepartures ?? null,
          retirements: v.retirements ?? null,
        };
        const promise =
          props.mode === "add"
            ? addOrganizationalMetric(payload)
            : updateOrganizationalMetric({
                uid: props.initialValues?.uid,
                rowVersion: props.initialValues?.rowVersion,
                ...payload,
              });
        return promise.then(() => {
          setLoading(false);
          props.onFinish?.();
        });
      })
      .catch((e) => {
        setLoading(false);
        if (e?.errorFields) return; // form validation errors render inline
        setError(true);
      });
  }

  const isAbsenteeism = metricType === "Absenteeism";
  const isTurnover = metricType === "Turnover";

  return (
    <Modal
      open={props.open}
      centered
      title={(props.mode === "add" ? "Add" : "Save") + " Metric Entry"}
      footer={null}
      maskClosable={false}
      onCancel={props.onCancel}
    >
      <Col className="flex max-h-[560px] w-[720px] flex-col overflow-hidden overflow-y-auto">
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          variant="filled"
          colon={false}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="departmentUid"
                label="Department"
                rules={[{ required: true, message: "Department is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Department"
                  options={departments}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="companyFiscalPeriodUid"
                label="Fiscal period (quarter)"
                rules={[{ required: true, message: "Fiscal period is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Period"
                  options={periods}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="metricType"
            label="Metric"
            rules={[{ required: true, message: "Metric is required" }]}
          >
            <Select
              placeholder="Metric"
              options={METRIC_TYPES.map((t) => ({ label: t, value: t }))}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="headcount"
                label="Headcount (period start)"
                rules={[{ required: true, message: "Headcount is required" }]}
              >
                <InputNumber className="w-full" min={0} />
              </Form.Item>
            </Col>
            {isAbsenteeism && (
              <Col span={12}>
                <Form.Item name="scheduledWorkdays" label="Scheduled workdays">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
            )}
          </Row>
          {isTurnover ? (
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="voluntaryDepartures" label="Voluntary">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="involuntaryDepartures" label="Involuntary">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="retirements" label="Retirements">
                  <InputNumber className="w-full" min={0} />
                </Form.Item>
              </Col>
            </Row>
          ) : (
            <Form.Item
              name="eventCount"
              label={
                isAbsenteeism
                  ? "Absent days"
                  : metricType === "Grievance"
                    ? "Grievances filed"
                    : metricType === "Accident"
                      ? "Accidents"
                      : "Event count"
              }
            >
              <InputNumber className="w-full" min={0} />
            </Form.Item>
          )}
          {error && (
            <Alert
              className="mb-2"
              message="Something went wrong, try again"
              type="error"
            />
          )}
          <Space className="sticky bottom-0 flex flex-row items-center justify-end">
            <Button disabled={loading} onClick={props.onCancel}>
              Cancel
            </Button>
            <Button type="primary" loading={loading} onClick={onSubmit}>
              {props.mode === "add" ? "Add" : "Save"}
            </Button>
          </Space>
        </Form>
      </Col>
    </Modal>
  );
}

export default OrganizationalMetricFormDialog;
